import type { CustomerGroupSettings } from "@cleanbudget/contract/price";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

/** The settings page's data: one query, which every write replaces with the settings it answers. */
export const customerGroupsQuery = () => orpc.price.listCustomerGroups.queryOptions();

/**
 * Every write of Instellingen → Klantgroepen. Each answers the whole `CustomerGroupSettings`, which
 * replaces the cached one; product editors (which show the groups' defaults and the shop's list)
 * are marked stale.
 */
export function useCustomerGroupWrites() {
	const queryClient = useQueryClient();
	const options = {
		onSuccess: (settings: CustomerGroupSettings) => {
			queryClient.setQueryData(customerGroupsQuery().queryKey, settings);
			void queryClient.invalidateQueries({ queryKey: orpc.product.get.key() });
		},
	};
	return {
		create: useMutation(orpc.price.createCustomerGroup.mutationOptions(options)),
		update: useMutation(orpc.price.updateCustomerGroup.mutationOptions(options)),
		remove: useMutation(orpc.price.deleteCustomerGroup.mutationOptions(options)),
		setShopQuantityDiscounts: useMutation(orpc.price.setShopQuantityDiscounts.mutationOptions(options)),
	};
}

export type CustomerGroupWrites = ReturnType<typeof useCustomerGroupWrites>;

/** A refused customer group write in Dutch, by the error's tag; anything else is "saving failed". */
export function useCustomerGroupErrorMessage() {
	const { t } = useTranslation("price");
	const { t: tCommon } = useTranslation();
	return (error: unknown): string =>
		match(apiErrorTag(error))
			.with("CustomerGroupNameTaken", () => t("error.nameTaken"))
			.with("ConsumerGroupFixed", () => t("error.consumerFixed"))
			.with("CustomerGroupNotFound", () => t("error.notFound"))
			.otherwise(() => tCommon("form.failed"));
}
